<script setup lang="ts">
import { MapPin as MapPinIcon } from '@lucide/vue'
import maplibregl, { type GeoJSONFeature } from 'maplibre-gl'
import {
  createVNode,
  inject,
  onMounted,
  onUnmounted,
  ref,
  render,
  watch,
  type Ref,
  type ShallowRef,
} from 'vue'

import PoiBrowseCard, { type PoiDestination } from '@/components/map/PoiBrowseCard.vue'
import { usePoiBrowse } from '@/composables/usePoiBrowse'
import {
  POI_BROWSE_KINDS,
  iconForKind,
  poiLabelForKind,
  poiTypeForKind,
} from '@/config/poiBrowseCategories'
import { parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { getPoiColor } from '@/services/poiService'
import { showError, showSuccess } from '@/services/toastService'
import { calculateHaversineDistance } from '@/services/trackMetricsCalculationService'
import { isSmallScreen } from '@/services/uiService'
import { useMapStore } from '@/stores/mapStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour } from '@/types/tour'
import { createMapMarker } from '@/utils/mapMarkerElement'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import { getEffectiveTourName } from '@/utils/tourName'
import { getBestTrackLineString } from '@/utils/trackGeometry'
import { Z_INDEX } from '@/utils/zIndex'

/**
 * gpx.studio-style POI browsing, drawn with RouteCamp's own orange POI markers. The POIs come from the
 * route.camp vector basemap (`base`/`pois` source) — the same offline tiles — queried by the `kind`s
 * the user switched on in the search panel (`usePoiBrowse`). We render our own markers (instead of the
 * basemap's zoom-gated symbols) so the selected POIs appear immediately and densely at any zoom, and
 * temporarily hide the matching basemap layers to avoid double icons. Hovering/tapping a marker shows
 * a card to add it to the collection (WORKSPACE_POI_CREATED). Renders nothing in the DOM tree itself.
 */
defineOptions({ name: 'PoiBrowseLayer' })

const MAX_MARKERS = 600
const SOURCE_ID = 'base'
const SOURCE_LAYER = 'pois'
// Routes whose nearest point is farther than this from the POI are unlikely add targets — dimmed.
const NEAR_ROUTE_THRESHOLD_M = 2000
// A browse candidate is treated as "the same POI" as an already-saved collection/tour POI when their
// names match and they sit within this distance. The basemap quantizes POI geometry per zoom, so the
// same POI shifts by tens of metres between the zoom it was saved at and the current display zoom —
// an exact-coordinate match would miss it and draw a duplicate pin on top of the saved one.
const SAME_POI_THRESHOLD_M = 50

const mapStore = useMapStore()
const workspaceStore = useWorkspaceStore()
const uiStore = useUiStore()

const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))

const { selectedKinds, selectedPoiCard, setSelectedPoiCard, clearSelectedPoiCard } = usePoiBrowse()

const markers = new Map<string, maplibregl.Marker>()
// Each marker's body element + colour, keyed by dedupe key, so we can highlight the "active" one
// (the POI whose card is currently open).
const markerBodies = new Map<string, { body: HTMLElement; color: string }>()
let cardPopup: maplibregl.Popup | null = null
let cardContainer: HTMLElement | null = null
let desktopPopupKey: string | null = null
let moveTimer: ReturnType<typeof setTimeout> | null = null

const markerKeyFor = (feature: GeoJSONFeature, lat: number, lon: number, kind: string) =>
  feature.id !== undefined ? `${feature.id}` : `${kind}:${lon}:${lat}`

// Whichever marker's card is open (mobile sheet selection, or the desktop popup) gets a coloured halo.
const activeMarkerKey = () =>
  isSmallScreen.value ? (selectedPoiCard.value?.markerKey ?? null) : desktopPopupKey

const applyActiveStyling = () => {
  const active = activeMarkerKey()
  for (const [key, { body, color }] of markerBodies) {
    // Halo only (no transform) so it doesn't fight the hover scale on the same element.
    body.style.boxShadow =
      key === active ? `0 4px 12px rgba(15,23,42,0.28), 0 0 0 4px ${color}59` : ''
  }
}

// Remove only the desktop popup (the mobile sheet is screen-anchored and survives map panning).
const closePopup = () => {
  if (cardContainer) {
    render(null, cardContainer)
    cardContainer = null
  }
  desktopPopupKey = null
  applyActiveStyling()
  cardPopup?.remove()
  cardPopup = null
}

// Fully dismiss the open card on either platform (popup + mobile sheet selection).
const dismissCard = () => {
  closePopup()
  clearSelectedPoiCard()
  applyActiveStyling()
}

const removeMarkers = () => {
  markers.forEach((marker) => marker.remove())
  markers.clear()
  markerBodies.clear()
}

const sameSpot = (
  point: { lat: number; lon: number; name?: string },
  lat: number,
  lon: number,
  name: string,
) =>
  (point.name ?? '') === name &&
  calculateHaversineDistance(point.lat, point.lon, lat, lon) <= SAME_POI_THRESHOLD_M

const alreadyInCollection = (lat: number, lon: number, name: string) =>
  workspaceStore.pois.some((poi) => sameSpot(poi, lat, lon, name))

const alreadyOnTour = (tourId: string, lat: number, lon: number, name: string) =>
  workspaceStore.tours
    .find((tour) => tour.clientId === tourId)
    ?.waypoints.some((wpt) => sameSpot(wpt, lat, lon, name)) ?? false

// A candidate is "added" once it lives anywhere in the collection — as a collection POI or as a
// waypoint on any route/activity — so it stops showing as an addable pin.
const isAlreadyAdded = (lat: number, lon: number, name: string) =>
  alreadyInCollection(lat, lon, name) ||
  workspaceStore.tours.some((tour) => tour.waypoints.some((wpt) => sameSpot(wpt, lat, lon, name)))

// Vector-tile rendering keys + `kind` (surfaced as the POI type) that aren't useful as extra tags.
const INTERNAL_KEYS = new Set([
  'min_zoom',
  'layer',
  'sort_rank',
  'pmap:kind',
  'pmap:min_zoom',
  'kind',
])
const isNameKey = (key: string) => key === 'name' || key === 'name_int' || key.startsWith('name:')

// Prefer the international / Latin-script name for the title so the card is readable regardless of
// the local script; fall back to the primary (often local-language) `name`, then the category label.
const NAME_KEYS = ['name:en', 'name:latin', 'name_int', 'name']
const resolveName = (properties: Record<string, unknown>, kind: string): string => {
  for (const key of NAME_KEYS) {
    const value = properties[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  for (const [key, value] of Object.entries(properties)) {
    if (isNameKey(key) && typeof value === 'string' && value.trim()) return value.trim()
  }
  return poiLabelForKind(kind)
}

// Meaningful extra properties (everything except the title + rendering internals). The primary
// `name` (usually the local-language label) is surfaced as a `name_local` attribute when it differs
// from the chosen title, so the local name is kept rather than lost. `pgf:*` keys are Protomaps'
// PGF-encoded glyph blobs — not human-readable text (they render as mojibake) — so they're dropped.
// `limit` caps the card preview; the stored POI keeps them all so no available information is lost.
const featureTags = (
  properties: Record<string, unknown>,
  displayName: string,
  limit?: number,
): Array<[string, string]> => {
  const tags: Array<[string, string]> = []
  const localName = properties.name
  if (typeof localName === 'string' && localName.trim() && localName.trim() !== displayName) {
    tags.push(['name_local', localName.trim()])
  }
  for (const [key, value] of Object.entries(properties)) {
    if (INTERNAL_KEYS.has(key) || isNameKey(key) || key.startsWith('pgf:')) continue
    if (value === null || value === undefined || value === '') continue
    if (typeof value === 'object') continue
    tags.push([key, String(value)])
    if (limit !== undefined && tags.length >= limit) break
  }
  return tags
}

// Common payload for adding a POI either to the collection (a highlight) or to a route/activity (a
// waypoint). Keeps every available tag; the card only shows a capped preview.
const buildPoiInput = (
  feature: GeoJSONFeature,
  lat: number,
  lon: number,
  name: string,
  kind: string,
) => ({
  lat,
  lon,
  name,
  description:
    featureTags(feature.properties ?? {}, name)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n') || undefined,
  type: poiTypeForKind(kind),
})

const addToCollection = async (
  feature: GeoJSONFeature,
  lat: number,
  lon: number,
  name: string,
  kind: string,
) => {
  if (!workspaceStore.clientId) throw new Error('No collection is open')
  try {
    // Route through the store action (not eventStore directly) so the new POI is pushed into the
    // reactive `pois` list immediately and shows in the collection's POI list without a reload.
    await workspaceStore.addWorkspacePOI(buildPoiInput(feature, lat, lon, name, kind))
    showSuccess(`Added ${name} to collection`)
    logService.info(LOG_CATEGORIES.UI, 'Added basemap POI to collection', { kind })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to add basemap POI to collection', { error })
    showError(
      `Failed to add point of interest: ${error instanceof Error ? error.message : String(error)}`,
    )
    throw error
  }
}

const addToTour = async (
  tourId: string,
  tourName: string,
  feature: GeoJSONFeature,
  lat: number,
  lon: number,
  name: string,
  kind: string,
) => {
  try {
    // Adds a waypoint on the route/activity (reactively pushed to tour.waypoints).
    await workspaceStore.addWaypoint(tourId, buildPoiInput(feature, lat, lon, name, kind))
    showSuccess(`Added ${name} to ${tourName}`)
    logService.info(LOG_CATEGORIES.UI, 'Added basemap POI as tour waypoint', { tourId, kind })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to add basemap POI as tour waypoint', { error })
    showError(
      `Failed to add point of interest: ${error instanceof Error ? error.message : String(error)}`,
    )
    throw error
  }
}

// Smallest distance (m) from the POI to any of the tour's tracks. Uses the 500 m-reduced geometry —
// coarse but plenty to rank "which route is this POI near?".
const minDistanceToTour = (tour: Tour, lat: number, lon: number): number => {
  let min = Infinity
  for (const track of tour.tracks ?? []) {
    const lineString = track.pointsLineReduced500m ?? getBestTrackLineString(track)
    if (!lineString) continue
    for (const [lng, plat] of parseLineString(lineString)) {
      const distance = calculateHaversineDistance(lat, lon, plat, lng)
      if (distance < min) min = distance
    }
  }
  return min
}

/**
 * Build the "Add to" destination list, ordered by context (the Komoot model — the collection holds
 * curated POIs, routes/activities hold contextual waypoints):
 * - Inside a route/activity → that route is the primary target; the collection drops to secondary.
 * - At collection level → the collection is primary, then routes/activities ranked by how near the
 *   POI is to each (nearest first), so the likely target surfaces without scrolling the full list.
 */
const buildDestinations = (
  feature: GeoJSONFeature,
  lat: number,
  lon: number,
  name: string,
  kind: string,
): PoiDestination[] => {
  // Outside an open collection (plain Map, Library, Collections overview) browsing is view-only —
  // there's nowhere to add the POI to, so the card shows its details without an "Add to" list.
  if (!workspaceStore.clientId) return []

  const collection: PoiDestination = {
    id: 'collection',
    label: 'Collection',
    kind: 'collection',
    alreadyAdded: alreadyInCollection(lat, lon, name),
    add: () => addToCollection(feature, lat, lon, name, kind),
  }

  const tourDestinations: PoiDestination[] = workspaceStore.allTours
    .map((tour) => ({ tour, distance: minDistanceToTour(tour, lat, lon) }))
    .sort((a, b) => a.distance - b.distance)
    .map(({ tour, distance }) => {
      const tourName = getEffectiveTourName(tour) || 'Unnamed route'
      return {
        id: tour.clientId,
        label: tourName,
        kind: 'tour' as const,
        color: resolveTourAccentColor(tour),
        // Routes that don't pass near this POI are unlikely targets — de-emphasise them.
        dimmed: distance > NEAR_ROUTE_THRESHOLD_M,
        alreadyAdded: alreadyOnTour(tour.clientId, lat, lon, name),
        add: () => addToTour(tour.clientId, tourName, feature, lat, lon, name, kind),
      }
    })

  const selectedTourId = workspaceStore.selectedTourId
  if (selectedTourId) {
    const selectedIndex = tourDestinations.findIndex((dest) => dest.id === selectedTourId)
    const selected = selectedIndex >= 0 ? tourDestinations.splice(selectedIndex, 1)[0] : undefined
    if (selected) {
      selected.primary = true
      selected.dimmed = false
      return [selected, collection, ...tourDestinations]
    }
  }

  collection.primary = true
  return [collection, ...tourDestinations]
}

const showCard = (
  feature: GeoJSONFeature,
  lat: number,
  lon: number,
  name: string,
  kind: string,
) => {
  const map = mapInstance?.value
  if (!map) return
  // The "Add to" card and a map drawer both own the mobile bottom — close any open drawer first.
  uiStore.closeMapDrawer()
  dismissCard()

  const shownTags = featureTags(feature.properties ?? {}, name, 5)
  const totalTags = featureTags(feature.properties ?? {}, name).length
  const cardProps = {
    name,
    typeLabel: poiLabelForKind(kind),
    icon: iconForKind(kind) ?? MapPinIcon,
    iconColor: getPoiColor(poiTypeForKind(kind)),
    coordsText: `${lat.toFixed(5)}°, ${lon.toFixed(5)}°`,
    tags: shownTags,
    hiddenTagCount: Math.max(0, totalTags - shownTags.length),
    destinations: buildDestinations(feature, lat, lon, name, kind),
  }

  const key = markerKeyFor(feature, lat, lon, kind)

  // Mobile: a draggable bottom sheet (PoiBrowseSheet) so the map stays visible. Desktop: a popup.
  if (isSmallScreen.value) {
    setSelectedPoiCard({ ...cardProps, lat, lon, markerKey: key })
    applyActiveStyling()
    return
  }

  desktopPopupKey = key
  applyActiveStyling()
  const container = document.createElement('div')
  cardContainer = container
  render(createVNode(PoiBrowseCard, { ...cardProps, close: () => closePopup() }), container)

  cardPopup = new maplibregl.Popup({
    offset: 18,
    maxWidth: 'none',
    className: 'poi-browse-popup',
    closeButton: false,
    closeOnClick: false,
  })
    .setLngLat([lon, lat])
    .setDOMContent(container)
    .addTo(map)

  // The Vue card is the surface; strip MapLibre's default popup chrome so only our card shows.
  const popupEl = cardPopup.getElement()
  const contentEl = popupEl?.querySelector<HTMLElement>('.maplibregl-popup-content')
  if (contentEl) {
    contentEl.style.padding = '0'
    contentEl.style.background = 'transparent'
    contentEl.style.boxShadow = 'none'
    contentEl.style.borderRadius = '0'
  }
  const tipEl = popupEl?.querySelector<HTMLElement>('.maplibregl-popup-tip')
  if (tipEl) tipEl.style.display = 'none'
  if (popupEl) popupEl.style.zIndex = Z_INDEX.POPOVER.toString()
}

// Candidate markers (not yet in the collection) are drawn OUTLINED — a white disc with a coloured
// ring + coloured icon — so they read clearly as "available to add", distinct from the solid,
// category-coloured markers used for POIs already saved to the collection (WorkspacePOIMarkerLayer).
const createMarkerElement = (
  kind: string,
  color: string,
): { container: HTMLDivElement; body: HTMLDivElement } => {
  const container = document.createElement('div')
  container.className = 'poi-browse-marker relative'
  container.style.zIndex = Z_INDEX.MAP_MARKERS_WAYPOINT.toString()
  container.style.cursor = 'pointer'

  const body = document.createElement('div')
  body.style.transition = 'transform 120ms ease, box-shadow 120ms ease'
  body.style.borderColor = color
  body.style.color = color
  body.className =
    'flex h-[28px] w-[28px] items-center justify-center rounded-full border-2 bg-white shadow-md'

  const iconEl = document.createElement('div')
  // lucide icons stroke with currentColor, which the body's color (the category colour) provides.
  render(createVNode(iconForKind(kind) ?? MapPinIcon, { size: 15, strokeWidth: 2.5 }), iconEl)
  body.appendChild(iconEl)
  container.appendChild(body)

  container.addEventListener('mouseenter', () => {
    body.style.transform = 'scale(1.15)'
  })
  container.addEventListener('mouseleave', () => {
    body.style.transform = 'scale(1)'
  })

  return { container, body }
}

// `querySourceFeatures` returns POIs from every loaded tile, including stale low-zoom ones still in
// the source cache whose point geometry is quantized to a coarse grid (hundreds of metres off). The
// rendered tiles are at the display zoom, so their geometry is accurate and matches the basemap
// symbol the user sees — index it by feature id to correct each POI's coordinate before we place a
// marker or save it to a collection/route.
const accuratePoiCoordsById = (map: maplibregl.Map): Map<string, [number, number]> => {
  const byId = new Map<string, [number, number]>()
  const poiLayerIds = map
    .getStyle()
    .layers.filter(
      (layer) =>
        'source' in layer &&
        layer.source === SOURCE_ID &&
        (layer as { 'source-layer'?: string })['source-layer'] === SOURCE_LAYER,
    )
    .map((layer) => layer.id)
  if (poiLayerIds.length === 0) return byId

  for (const feature of map.queryRenderedFeatures({ layers: poiLayerIds })) {
    if (feature.id === undefined || feature.geometry.type !== 'Point') continue
    byId.set(String(feature.id), feature.geometry.coordinates as [number, number])
  }
  return byId
}

const renderMarkers = () => {
  const map = mapInstance?.value
  if (!map || !mapLoaded.value) return

  removeMarkers()

  const kinds = Array.from(selectedKinds.value)
  if (kinds.length === 0) return
  if (!map.getSource(SOURCE_ID)) return

  const features = map.querySourceFeatures(SOURCE_ID, {
    sourceLayer: SOURCE_LAYER,
    filter: ['in', ['get', 'kind'], ['literal', kinds]],
  })
  const accurateCoords = accuratePoiCoordsById(map)

  const seen = new Set<string>()
  for (const feature of features) {
    if (feature.geometry.type !== 'Point') continue
    const accurate = feature.id !== undefined ? accurateCoords.get(String(feature.id)) : undefined
    const [lon, lat] = accurate ?? (feature.geometry.coordinates as [number, number])
    const kind = String(feature.properties?.kind ?? '')
    const dedupeKey = markerKeyFor(feature, lat, lon, kind)
    if (seen.has(dedupeKey)) continue
    seen.add(dedupeKey)
    if (markers.size >= MAX_MARKERS) break

    const name = resolveName(feature.properties ?? {}, kind)
    // Don't draw a candidate for a POI already saved (collection POI or a route/activity waypoint).
    if (isAlreadyAdded(lat, lon, name)) continue

    const color = getPoiColor(poiTypeForKind(kind))
    const { container: element, body } = createMarkerElement(kind, color)
    element.addEventListener('click', (event) => {
      event.stopPropagation()
      showCard(feature, lat, lon, name, kind)
    })
    element.addEventListener('mouseenter', () => showCard(feature, lat, lon, name, kind))

    const marker = createMapMarker({ element, anchor: 'center' }).setLngLat([lon, lat]).addTo(map)
    markers.set(dedupeKey, marker)
    markerBodies.set(dedupeKey, { body, color })
  }

  // Re-apply the active halo (e.g. after panning while the mobile sheet stays open).
  applyActiveStyling()
}

// For the kinds we render ourselves we drop only the basemap *icon* (it would double up with our own
// marker) while keeping the basemap *label* — icon and text share one symbol layer, so we toggle
// `icon-opacity` instead of hiding the whole layer. The layer's overall visibility still follows the
// user's Map Layers preference.
const syncBasemapVisibility = () => {
  const map = mapInstance?.value
  if (!map) return
  for (const kind of POI_BROWSE_KINDS) {
    const layerId = `pois_${kind}`
    if (!map.getLayer(layerId)) continue
    const browsing = selectedKinds.value.has(kind)
    const userVisible = mapStore.layerVisibility[layerId] !== false
    try {
      map.setLayoutProperty(layerId, 'visibility', userVisible ? 'visible' : 'none')
      map.setPaintProperty(layerId, 'icon-opacity', browsing ? 0 : 1)
    } catch {
      // Layer may briefly not exist during a style swap; the next revision re-applies.
      logService.warn(LOG_CATEGORIES.MAP, 'Failed to set POI layer visibility', { layerId })
    }
  }
}

const refresh = () => {
  // Only the desktop popup closes on pan/zoom; the mobile sheet is screen-anchored and stays.
  closePopup()
  syncBasemapVisibility()
  renderMarkers()
}

const scheduleRefresh = () => {
  if (moveTimer) clearTimeout(moveTimer)
  moveTimer = setTimeout(refresh, 150)
}

const attach = () => {
  const map = mapInstance?.value
  if (!map) return
  map.on('moveend', scheduleRefresh)
  refresh()
}

const detach = () => {
  const map = mapInstance?.value
  if (map) map.off('moveend', scheduleRefresh)
  if (moveTimer) clearTimeout(moveTimer)
  removeMarkers()
  dismissCard()
}

// Changing the browse selection dismisses any open add card, then re-renders.
watch(selectedKinds, () => {
  dismissCard()
  refresh()
})
watch(() => mapStyleRevision.value, scheduleRefresh)
// When a POI is added/removed (collection POI or a route/activity waypoint), re-render so the
// just-added candidate is dropped (it now shows as a saved marker), without disturbing the open card.
watch(
  () =>
    workspaceStore.pois.length +
    workspaceStore.tours.reduce((total, tour) => total + tour.waypoints.length, 0),
  renderMarkers,
)
watch(
  () => mapLoaded.value,
  (loaded) => {
    if (loaded) attach()
  },
  { immediate: true },
)

onMounted(() => {
  if (mapLoaded.value) attach()
})

onUnmounted(() => {
  // Leaving the collection: drop our markers and restore every basemap POI layer.
  const map = mapInstance?.value
  detach()
  if (map) {
    for (const kind of POI_BROWSE_KINDS) {
      const layerId = `pois_${kind}`
      if (!map.getLayer(layerId)) continue
      const restoreVisible = mapStore.layerVisibility[layerId] !== false
      try {
        map.setLayoutProperty(layerId, 'visibility', restoreVisible ? 'visible' : 'none')
        map.setPaintProperty(layerId, 'icon-opacity', 1)
      } catch {
        logService.warn(LOG_CATEGORIES.MAP, 'Failed to restore POI layer visibility', { layerId })
      }
    }
  }
})
</script>

<template>
  <div style="display: none" />
</template>
